/**
 * Sliding puzzle ghost-guide helpers (A* / greedy next moves).
 * Layout model matches SlidingPlayMain: layout[visual] = slice index; empty = n-1.
 */

function gridCellsAdjacent(gs, indexA, indexB) {
    if (indexA < 0 || indexB < 0) return false;
    const ra = Math.floor(indexA / gs);
    const ca = indexA % gs;
    const rb = Math.floor(indexB / gs);
    const cb = indexB % gs;
    return Math.abs(ra - rb) + Math.abs(ca - cb) === 1;
}

function normalizeLayout(layout, gridSize) {
    const gs = Number(gridSize);
    const n = Number.isFinite(gs) && gs > 0 ? gs * gs : 0;
    if (!Array.isArray(layout) || n <= 0 || layout.length !== n) return [];
    const nums = layout.map((x) => Number(x));
    return nums.every(Number.isFinite) ? nums : [];
}

function defaultEmptyTileIndex(gridSize) {
    const n = Number(gridSize) > 0 ? Number(gridSize) * Number(gridSize) : 0;
    return n > 0 ? n - 1 : -1;
}

function emptyVisualIndex(layout, emptyIdx) {
    return layout.findIndex((v) => Number(v) === emptyIdx);
}

function applyMoveAtVisual(layout, visualCellIdx, gridSize, emptyIdx) {
    const gs = Number(gridSize);
    const emptyVisual = emptyVisualIndex(layout, emptyIdx);
    if (emptyVisual < 0 || visualCellIdx === emptyVisual) return null;
    if (!gridCellsAdjacent(gs, visualCellIdx, emptyVisual)) return null;
    const next = [...layout];
    [next[visualCellIdx], next[emptyVisual]] = [next[emptyVisual], next[visualCellIdx]];
    return next;
}

function neighborsOfEmpty(layout, gridSize, emptyIdx) {
    const gs = Number(gridSize);
    const emptyVisual = emptyVisualIndex(layout, emptyIdx);
    if (emptyVisual < 0) return [];
    const out = [];
    for (let i = 0; i < layout.length; i += 1) {
        if (i !== emptyVisual && gridCellsAdjacent(gs, i, emptyVisual)) out.push(i);
    }
    return out;
}

function layoutManhattanHeuristic(layout, gs, emptyTileIndex) {
    let sum = 0;
    for (let i = 0; i < layout.length; i += 1) {
        const tile = Number(layout[i]);
        if (tile === emptyTileIndex) continue;
        const goalRow = Math.floor(tile / gs);
        const goalCol = tile % gs;
        const row = Math.floor(i / gs);
        const col = i % gs;
        sum += Math.abs(goalRow - row) + Math.abs(goalCol - col);
    }
    return sum;
}

function popLowestCostNode(open) {
    let best = 0;
    for (let i = 1; i < open.length; i += 1) {
        const node = open[i];
        const bestNode = open[best];
        if (node.f < bestNode.f || (node.f === bestNode.f && node.g < bestNode.g)) {
            best = i;
        }
    }
    return open.splice(best, 1)[0];
}

function delay(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

/** A* click sequence (visual cell indices). null if no path within maxNodes. */
async function findSolveClickSequenceAsync(startLayout, gridSize, emptyIdx, maxNodes = 40000) {
    const gs = Number(gridSize);
    const layout = normalizeLayout(startLayout, gs);
    const n = layout.length;
    if (n === 0) return null;
    const empty = emptyIdx ?? defaultEmptyTileIndex(gs);
    const goalKey = Array.from({ length: n }, (_, i) => i).join(",");
    const startKey = layout.join(",");
    if (startKey === goalKey) return [];

    await delay(0);

    const open = [
        {
            layout,
            moves: [],
            g: 0,
            f: layoutManhattanHeuristic(layout, gs, empty),
        },
    ];
    const bestG = new Map([[startKey, 0]]);
    let expanded = 0;
    const yieldEvery = 500;

    while (open.length > 0 && expanded < maxNodes) {
        const { layout: current, moves, g } = popLowestCostNode(open);
        expanded += 1;
        if (expanded % yieldEvery === 0) await delay(0);

        const key = current.join(",");
        const seenG = bestG.get(key);
        if (seenG != null && g > seenG) continue;
        if (key === goalKey) return moves;

        const emptyVisual = emptyVisualIndex(current, empty);
        for (let visual = 0; visual < n; visual += 1) {
            if (visual === emptyVisual) continue;
            if (!gridCellsAdjacent(gs, visual, emptyVisual)) continue;
            const next = [...current];
            [next[visual], next[emptyVisual]] = [next[emptyVisual], next[visual]];
            const nextKey = next.join(",");
            const nextG = g + 1;
            const prevG = bestG.get(nextKey);
            if (prevG != null && nextG >= prevG) continue;
            bestG.set(nextKey, nextG);
            open.push({
                layout: next,
                moves: [...moves, visual],
                g: nextG,
                f: nextG + layoutManhattanHeuristic(next, gs, empty),
            });
        }
    }
    return null;
}

/** Greedy: repeatedly pick the neighbor that most reduces Manhattan distance. */
function greedyClickSequence(startLayout, gridSize, emptyIdx, count) {
    const gs = Number(gridSize);
    let current = normalizeLayout(startLayout, gs);
    const empty = emptyIdx ?? defaultEmptyTileIndex(gs);
    if (current.length === 0) return [];
    const moves = [];
    let last = null;
    for (let step = 0; step < count; step += 1) {
        if (current.every((v, i) => Number(v) === i)) break;
        let options = neighborsOfEmpty(current, gs, empty);
        if (last != null && options.length > 1) {
            options = options.filter((v) => v !== last);
        }
        let best = null;
        let bestH = Infinity;
        for (const visual of options) {
            const next = applyMoveAtVisual(current, visual, gs, empty);
            if (!next) continue;
            const h = layoutManhattanHeuristic(next, gs, empty);
            if (h < bestH) {
                bestH = h;
                best = visual;
            }
        }
        if (best == null) break;
        const next = applyMoveAtVisual(current, best, gs, empty);
        if (!next) break;
        moves.push(best);
        last = emptyVisualIndex(current, empty);
        current = next;
    }
    return moves;
}

/**
 * Build up to `maxMoves` ghost guide steps from the current board.
 * Each step: tile at `from` slides into empty at `to` (sliceIndex = tile id).
 * @returns {Promise<Array<{ from: number, to: number, sliceIndex: number }>>}
 */
export async function buildGhostGuideMoves(startLayout, gridSize, emptyIdx, maxMoves = 5) {
    const gs = Number(gridSize);
    const empty = emptyIdx ?? defaultEmptyTileIndex(gs);
    let clicks = await findSolveClickSequenceAsync(startLayout, gs, empty);
    if (clicks == null) {
        clicks = greedyClickSequence(startLayout, gs, empty, maxMoves);
    }
    if (!Array.isArray(clicks) || clicks.length === 0) return [];

    const steps = [];
    let current = normalizeLayout(startLayout, gs);
    for (let i = 0; i < clicks.length && steps.length < maxMoves; i += 1) {
        const from = clicks[i];
        const to = emptyVisualIndex(current, empty);
        if (to < 0) break;
        const sliceIndex = Number(current[from]);
        const next = applyMoveAtVisual(current, from, gs, empty);
        if (!next) break;
        steps.push({ from, to, sliceIndex });
        current = next;
    }
    return steps;
}

/**
 * Instant ghost-guide steps for marketing screenshots (greedy only — no A* wait).
 * @returns {Array<{ from: number, to: number, sliceIndex: number }>}
 */
export function buildGhostGuideMovesFast(startLayout, gridSize, emptyIdx, maxMoves = 4) {
    const gs = Number(gridSize);
    const empty = emptyIdx ?? defaultEmptyTileIndex(gs);
    const clicks = greedyClickSequence(startLayout, gs, empty, maxMoves);
    if (!Array.isArray(clicks) || clicks.length === 0) return [];

    const steps = [];
    let current = normalizeLayout(startLayout, gs);
    for (let i = 0; i < clicks.length && steps.length < maxMoves; i += 1) {
        const from = clicks[i];
        const to = emptyVisualIndex(current, empty);
        if (to < 0) break;
        const sliceIndex = Number(current[from]);
        const next = applyMoveAtVisual(current, from, gs, empty);
        if (!next) break;
        steps.push({ from, to, sliceIndex });
        current = next;
    }
    return steps;
}

export const GHOST_HINT_MOVE_COUNT = 5;
export const GHOST_HINT_STEP_MS = 850;
export const GHOST_HINT_GAP_MS = 180;
export const GHOST_HINT_ALL_MS = 3000;
